import {
  HHBox,
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { ArrowBack, ChevronRight } from '@mui/icons-material';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ClaimsIngestionView } from '../claims-ingestion/components/claims-ingestion-view';
import { HingeConnectLayout } from '../hinge-connect/components/hinge-connect-layout';
import { useROIRoleBasedRoutes } from '../hooks/use-roi-role-based-routes';
import { PartnerDataGatewayModule } from '../partner-data-gateway';
import { BasePathProvider } from '../partner-data-gateway/context/base-path-context';
import { PermissionsProvider } from '../partner-data-gateway/context/permissions-context';
import { ROIDashboard } from './roi-dashboard';

// Navigation breadcrumb component
const ROINavigation = (): JSX.Element | null => {
  const location = useLocation();
  const navigate = useNavigate();

  const getNavigationConfig = (): {
    showNav: boolean;
    currentPage?: string;
    description?: string;
  } => {
    if (location.pathname.includes('/hinge-connect')) {
      return {
        showNav: true,
        currentPage: 'Hinge Connect',
        description: 'Rules management, user tags, and member data services',
      };
    }
    if (location.pathname.includes('/claims-ingestion')) {
      return {
        showNav: true,
        currentPage: 'Claims Ingestion',
        description: 'Configure data provider file ingestion settings',
      };
    }
    if (location.pathname.includes('/partner-data-gateway')) {
      return {
        showNav: true,
        currentPage: 'Partner Data Gateway',
        description: 'File ingestion monitoring and configuration management',
      };
    }
    return { showNav: false };
  };

  const navConfig = getNavigationConfig();

  if (!navConfig.showNav) {
    return null;
  }

  return (
    <HHBox
      sx={{ backgroundColor: '#ffffff', borderBottom: '1px solid #E0E0E0' }}
    >
      <HHStack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ paddingX: '2rem', paddingY: '1rem' }}
      >
        <HHButton
          hhVariant="text"
          startIcon={<ArrowBack />}
          onClick={(): void => navigate('/roi')}
          sx={{
            color: '#1565C0',
            fontWeight: 500,
            textTransform: 'none',
            padding: '6px 12px',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#E3F2FD',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          ROI Dashboard
        </HHButton>
        <ChevronRight sx={{ color: 'text.secondary', fontSize: '20px' }} />
        <HHTypography
          hhVariant="body1"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          {navConfig.currentPage}
        </HHTypography>
      </HHStack>
    </HHBox>
  );
};

export const ROILayout = (): JSX.Element => {
  const routeAccess = useROIRoleBasedRoutes();

  return (
    <HHBox>
      <ROINavigation />
      <Routes>
        {/* ROI Dashboard - default route */}
        <Route path="/" element={<ROIDashboard />} />

        {/* Hinge Connect workflow - conditionally rendered based on roles */}
        {(routeAccess.hingeConnectRules ||
          routeAccess.hingeConnectTags ||
          routeAccess.hingeConnectAssessmentTasks ||
          routeAccess.hingeConnectRuleAssessments ||
          routeAccess.hingeConnectHealthRecords ||
          routeAccess.hingeConnectUserTags) && (
          <Route path="/hinge-connect/*" element={<HingeConnectLayout />} />
        )}

        {/* Claims Ingestion workflow - conditionally rendered based on roles */}
        {routeAccess.claimsIngestion && (
          <Route path="/claims-ingestion" element={<ClaimsIngestionView />} />
        )}

        {/* Partner Data Gateway workflow - conditionally rendered based on roles */}
        {routeAccess.partnerDataGateway && (
          <Route
            path="/partner-data-gateway/*"
            element={
              <PermissionsProvider
                permissions={{
                  canViewIngestions: routeAccess.partnerDataGateway,
                  canControlIngestions: routeAccess.partnerDataGatewayAdmin,
                  canViewConfigurations: routeAccess.partnerDataGateway,
                  canManageConfigurations: routeAccess.partnerDataGatewayAdmin,
                  canViewPhi: routeAccess.partnerDataGatewayPhi,
                }}
              >
                <BasePathProvider basePath="/roi/partner-data-gateway">
                  <PartnerDataGatewayModule />
                </BasePathProvider>
              </PermissionsProvider>
            }
          />
        )}

        {/* Redirect any unknown routes to dashboard */}
        <Route path="*" element={<Navigate to="/roi" />} />
      </Routes>
    </HHBox>
  );
};
